import { useLogger } from "@bindery/logging/react";
import { Button } from "@bindery/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bindery/ui/components/ui/dropdown-menu";
import { cn } from "@bindery/ui/lib/utils";
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowRightEndOnRectangleIcon,
  BookmarkIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/20/solid";
import {
  createContext,
  type CSSProperties,
  Fragment,
  type ReactNode,
  type Ref,
  use,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { SIGN_IN_PAGE, saveProgressHref } from "@/auth/links";
import type { NavPalette } from "@/components/design-switcher";
import { type SessionStore, sessionStore, useSession } from "@/convex/session";
import { SELECT_READER_PATH } from "@/routes/require-reader";
import { useNickname } from "@/storage/nickname";
import { NicknameDialog } from "./nickname-dialog";
import { ResetProgressDialog } from "./reset-progress-dialog";
import { userMenuViewOf } from "./session-view";
import { SIGN_OUT_PATH } from "@/routes/sign-out";

const MENU_ICON_CLASS = "mr-2 h-4 w-4";
const MENU_CONTENT_CLASS =
  "z-[70] w-52 border-white/10 bg-black/80 text-white backdrop-blur-2xl";
const MENU_ITEM_CLASS =
  "cursor-pointer text-white/80 focus:bg-white/[0.08] focus:text-white";
const INLINE_ITEM_CLASS =
  "flex h-12 w-full items-center gap-2 rounded-lg px-3 text-left text-sm text-white/80 transition-colors hover:bg-white/[0.08]";

function paletteStyle(palette: NavPalette | undefined) {
  return palette
    ? ({
        color: palette.textMuted,
        "--nav-link-hover-bg": palette.surfaceHover,
      } as CSSProperties)
    : undefined;
}

const DrawerNavigation = createContext<(() => void) | undefined>(undefined);

export function UserMenu({
  inline = false,
  onNavigate,
  palette,
  store = sessionStore,
}: {
  /** The mobile drawer's variant: items in a column, not a dropdown. */
  inline?: boolean;
  /** Called on every navigation from the inline menu, to close the drawer. */
  onNavigate?: () => void;
  palette?: NavPalette;
  store?: SessionStore;
}) {
  return (
    <DrawerNavigation value={onNavigate}>
      <UserMenuForSession inline={inline} palette={palette} store={store} />
    </DrawerNavigation>
  );
}

function UserMenuForSession({
  inline,
  palette,
  store,
}: {
  inline: boolean;
  palette?: NavPalette;
  store: SessionStore;
}) {
  const view = userMenuViewOf(useSession(store));
  switch (view.kind) {
    case "pending":
      return null;
    case "sign-in":
      return inline ? <SignInInline /> : <SignInButton palette={palette} />;
    case "account-without-reader":
      return <AccountWithoutReaderMenu inline={inline} palette={palette} />;
    case "unlinked-reader":
      return (
        <UnlinkedReaderMenu
          readerId={view.readerId}
          linkKnown={view.linkKnown}
          inline={inline}
          palette={palette}
        />
      );
    case "signed-in-reader":
      return (
        <SignedInReaderMenu
          readerId={view.readerId}
          inline={inline}
          palette={palette}
        />
      );
  }
}

function SignInInline() {
  const onNavigate = use(DrawerNavigation);
  return (
    <Link
      to={SIGN_IN_PAGE}
      onClick={onNavigate}
      className="flex h-12 items-center justify-center gap-2 rounded-lg bg-white/10 text-sm font-medium text-white/90 transition-colors hover:bg-white/[0.15]"
    >
      <ArrowRightEndOnRectangleIcon className="h-4 w-4" />
      Sign In
    </Link>
  );
}

function SignInButton({ palette }: { palette?: NavPalette }) {
  return (
    <Button
      asChild
      size="sm"
      style={paletteStyle(palette)}
      className={cn(
        "rounded-xl text-[13px] font-semibold tracking-wide",
        palette
          ? "bg-transparent hover:bg-[var(--nav-link-hover-bg)]"
          : "bg-white/10 text-white/90 hover:bg-white/[0.15]",
      )}
    >
      <Link to={SIGN_IN_PAGE}>
        <ArrowRightEndOnRectangleIcon className={MENU_ICON_CLASS} />
        Sign In
      </Link>
    </Button>
  );
}

function UserMenuTrigger({
  palette,
  triggerRef,
}: {
  palette?: NavPalette;
  triggerRef?: Ref<HTMLButtonElement>;
}) {
  return (
    <DropdownMenuTrigger asChild>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Account menu"
        style={paletteStyle(palette)}
        className={cn(
          "flex items-center gap-1.5 rounded-xl px-2 py-1.5 text-[13px] font-semibold tracking-wide transition-all duration-150",
          palette
            ? "hover:bg-[var(--nav-link-hover-bg)]"
            : "text-white/60 hover:bg-white/[0.06] hover:text-white/90",
        )}
      >
        <UserIcon className="h-15 w-15 shrink-0" />
      </button>
    </DropdownMenuTrigger>
  );
}

/** One entry, as a dropdown item or as a drawer row. */
interface MenuEntry {
  key: string;
  icon: ReactNode;
  label: ReactNode;
  destructive?: boolean;
  to?: string;
  /** Leaves the page, so the drawer holding the menu must close. */
  navigates?: boolean;
  onSelect?: () => void;
  separatorBefore?: boolean;
}

function MenuEntries({
  entries,
  heading,
  inline,
  palette,
  triggerRef,
}: {
  entries: readonly MenuEntry[];
  heading?: string;
  inline: boolean;
  palette?: NavPalette;
  triggerRef?: Ref<HTMLButtonElement>;
}) {
  if (inline) {
    const onNavigate = use(DrawerNavigation);
    return (
      <div className="flex flex-col gap-1">
        {heading && (
          <div className="px-3 py-2 text-sm text-white/50">{heading}</div>
        )}
        {entries.map((entry) => {
          const className = cn(
            INLINE_ITEM_CLASS,
            entry.destructive && "text-red-400",
          );
          const body = (
            <>
              {entry.icon}
              {entry.label}
            </>
          );
          const select = () => {
            entry.onSelect?.();
            if (entry.to !== undefined || entry.navigates) onNavigate?.();
          };
          if (entry.to)
            return (
              <Link
                key={entry.key}
                to={entry.to}
                onClick={select}
                className={className}
              >
                {body}
              </Link>
            );
          return (
            <button
              key={entry.key}
              type="button"
              onClick={select}
              className={className}
            >
              {body}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <DropdownMenu>
      <UserMenuTrigger palette={palette} triggerRef={triggerRef} />
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={MENU_CONTENT_CLASS}
      >
        {heading && (
          <>
            <DropdownMenuItem disabled className="text-white/50">
              {heading}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
          </>
        )}
        {entries.map((entry) => {
          const className = entry.destructive
            ? "cursor-pointer text-red-400 focus:bg-white/[0.08] focus:text-red-300"
            : MENU_ITEM_CLASS;
          const item = entry.to ? (
            <DropdownMenuItem asChild className={className}>
              <Link
                to={entry.to}
                onClick={entry.onSelect}
                className="flex items-center"
              >
                {entry.icon}
                {entry.label}
              </Link>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={entry.onSelect} className={className}>
              {entry.icon}
              {entry.label}
            </DropdownMenuItem>
          );
          return (
            <Fragment key={entry.key}>
              {entry.separatorBefore && (
                <DropdownMenuSeparator className="bg-white/10" />
              )}
              {item}
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useSignOutEntry(source: string): MenuEntry {
  const log = useLogger();
  return {
    key: "sign-out",
    icon: <ArrowLeftStartOnRectangleIcon className={MENU_ICON_CLASS} />,
    label: "Sign Out",
    to: SIGN_OUT_PATH,
    separatorBefore: true,
    onSelect: () => log.event("auth.sign_out.initiated", { source }),
  };
}

function AccountWithoutReaderMenu({
  inline,
  palette,
}: {
  inline: boolean;
  palette?: NavPalette;
}) {
  const signOut = useSignOutEntry(
    inline ? "user_menu_mobile" : "user_menu_desktop",
  );
  return (
    <MenuEntries
      inline={inline}
      palette={palette}
      heading={inline ? undefined : "Signed in"}
      entries={[
        {
          key: "choose-reader",
          icon: <UsersIcon className={MENU_ICON_CLASS} />,
          label: "Choose a reader",
          to: SELECT_READER_PATH,
        },
        signOut,
      ]}
    />
  );
}

function SignedInReaderMenu({
  readerId,
  inline,
  palette,
}: {
  readerId: string;
  inline: boolean;
  palette?: NavPalette;
}) {
  const nickname = useNickname(readerId);
  const signOut = useSignOutEntry(
    inline ? "user_menu_mobile" : "user_menu_desktop",
  );
  return (
    <MenuEntries
      inline={inline}
      palette={palette}
      heading={`Signed in as ${nickname ?? "Reader"}`}
      entries={[{ ...signOut, separatorBefore: false }]}
    />
  );
}

type ReaderDialog = "nickname" | "reset" | null;

// A reader whose link is unknown may be an account's: it gets ../app's
// anonymous items, and a way out as well.
function UnlinkedReaderMenu({
  readerId,
  linkKnown,
  inline,
  palette,
}: {
  readerId: string;
  linkKnown: boolean;
  inline: boolean;
  palette?: NavPalette;
}) {
  const [dialog, setDialog] = useState<ReaderDialog>(null);
  const signOut = useSignOutEntry(
    inline ? "user_menu_mobile" : "user_menu_desktop",
  );
  const nickname = useNickname(readerId);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const log = useLogger();
  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const triggerRef = useRef<HTMLButtonElement>(null);
  const returnFocusToMenu = (event: Event) => {
    if (triggerRef.current === null) return;
    event.preventDefault();
    triggerRef.current.focus();
  };

  return (
    <>
      <MenuEntries
        inline={inline}
        palette={palette}
        triggerRef={triggerRef}
        entries={[
          {
            key: "nickname",
            icon: <PencilIcon className={MENU_ICON_CLASS} />,
            label: nickname ? "Change name" : "Add a name",
            onSelect: () => setDialog("nickname"),
          },
          {
            key: "save",
            icon: <BookmarkIcon className={MENU_ICON_CLASS} />,
            label: "Save your reading journey",
            onSelect: () => {
              log.event("auth.redirect.initiated", {
                source: "anonymous_menu",
                has_progress: true,
              });
              void navigate(saveProgressHref(pathname));
            },
            navigates: true,
          },
          {
            key: "reset",
            icon: <TrashIcon className={MENU_ICON_CLASS} />,
            label: "Reset all progress",
            destructive: true,
            separatorBefore: true,
            onSelect: () => setDialog("reset"),
          },
          ...(linkKnown ? [] : [{ ...signOut, separatorBefore: false }]),
        ]}
      />
      <NicknameDialog
        key={dialog === "nickname" ? "open" : "closed"}
        open={dialog === "nickname"}
        onOpenChange={close}
        readerId={readerId}
        initialValue={nickname}
        onCloseAutoFocus={returnFocusToMenu}
      />
      <ResetProgressDialog
        open={dialog === "reset"}
        onOpenChange={close}
        readerId={readerId}
        onCloseAutoFocus={returnFocusToMenu}
      />
    </>
  );
}
